'use client';

import type {
  PortalActionTokenState,
  PortalPasswordMutationApiResponse,
  PortalRequestPasswordResetApiResponse,
} from 'src/auth/types';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';

import { PasswordIcon } from 'src/assets/icons';

import { Form, Field } from 'src/components/hook-form';

import { FormHead } from 'src/auth/components/form-head';
import { FormReturnLink } from 'src/auth/components/form-return-link';
import { PORTAL_PASSWORD_MIN_LENGTH, PORTAL_PASSWORD_MIN_LENGTH_HINT } from 'src/auth/constants';
import {
  portalCompletePasswordResetSchema,
  portalForgotPasswordRequestSchema,
} from 'src/auth/schema';
import {
  getTokenStateMessage,
  getAuthRateLimitedMessage,
  getAuthUnavailableMessage,
} from 'src/auth/error-message';

type PortalForgotPasswordViewProps = {
  initialTokenState?: Exclude<PortalActionTokenState, 'READY'> | null;
  token?: string | null;
};

export function PortalForgotPasswordView({
  token = null,
  initialTokenState = null,
}: PortalForgotPasswordViewProps) {
  return token || initialTokenState ? (
    <PortalForgotPasswordResetMode initialTokenState={initialTokenState} token={token} />
  ) : (
    <PortalForgotPasswordRequestMode />
  );
}

function PortalForgotPasswordRequestMode() {
  const [submitState, setSubmitState] = useState<'idle' | 'success' | 'error'>('idle');

  const methods = useForm({
    resolver: zodResolver(portalForgotPasswordRequestSchema),
    defaultValues: {
      email: '',
    },
  });

  const {
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (values) => {
    setSubmitState('idle');

    const response = await fetch('/api/portal-auth/request-password-reset/', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      referrerPolicy: 'no-referrer',
      body: JSON.stringify(values),
    });

    const result = (await response.json()) as PortalRequestPasswordResetApiResponse;

    if (result.ok) {
      methods.reset();
      setSubmitState('success');
      return;
    }

    if (result.error === 'validation_error' && result.fieldErrors) {
      Object.entries(result.fieldErrors).forEach(([field, message]) => {
        if (message) {
          setError(field as 'email', { message, type: 'server' });
        }
      });
      return;
    }

    setSubmitState('error');
  });

  return (
    <>
      <FormHead
        icon={<PasswordIcon />}
        title="Wachtwoord opnieuw instellen"
        description="Vraag een nieuwe resetlink aan voor je klantenportaalaccount."
      />

      {submitState === 'success' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Als er een portaalaccount bestaat voor dit e-mailadres, ontvang je instructies om je
          wachtwoord opnieuw in te stellen.
        </Alert>
      )}

      {submitState === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {getAuthUnavailableMessage('reset')}
        </Alert>
      )}

      <Form methods={methods} onSubmit={onSubmit}>
        <Box sx={{ gap: 3, display: 'flex', flexDirection: 'column' }}>
          <Field.Text
            name="email"
            label="E-mailadres"
            placeholder="naam@bedrijf.nl"
            autoFocus
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Button
            fullWidth
            color="primary"
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            loadingIndicator="Aanvraag verzenden..."
          >
            Resetlink aanvragen
          </Button>
        </Box>
      </Form>

      <FormReturnLink href={paths.login} label="Terug naar inloggen" />
    </>
  );
}

function PortalForgotPasswordResetMode({
  token,
  initialTokenState = null,
}: {
  token: string | null;
  initialTokenState?: Exclude<PortalActionTokenState, 'READY'> | null;
}) {
  const [serverError, setServerError] = useState<string | null>(null);
  const [tokenState, setTokenState] = useState<Exclude<PortalActionTokenState, 'READY'> | null>(
    initialTokenState
  );

  const methods = useForm({
    resolver: zodResolver(portalCompletePasswordResetSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const {
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);

    const response = await fetch('/api/portal-auth/complete-password-reset/', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      referrerPolicy: 'no-referrer',
      body: JSON.stringify({ token, ...values }),
    });

    const result = (await response.json()) as PortalPasswordMutationApiResponse;

    if (result.ok) {
      window.location.assign(result.redirectTo);
      return;
    }

    if (result.error === 'validation_error' && result.fieldErrors) {
      Object.entries(result.fieldErrors).forEach(([field, message]) => {
        if (message) {
          setError(field as 'password' | 'confirmPassword', { message, type: 'server' });
        }
      });
      return;
    }

    if (result.error === 'password_policy') {
      setError('password', {
        message: result.message || 'Het gekozen wachtwoord voldoet niet aan de vereisten.',
        type: 'server',
      });
      return;
    }

    if (result.error === 'rate_limited') {
      setServerError(result.message || getAuthRateLimitedMessage('reset'));
      return;
    }

    if (result.error === 'token_state' && result.tokenState) {
      setTokenState(result.tokenState);
      return;
    }

    setServerError(getAuthUnavailableMessage('reset'));
  });

  const tokenStateMessage = tokenState ? getTokenStateMessage('reset', tokenState) : null;

  if (tokenStateMessage) {
    return (
      <>
        <FormHead
          icon={<PasswordIcon />}
          title={tokenStateMessage.title}
          description={tokenStateMessage.description}
        />

        <Alert severity="warning" sx={{ mb: 3 }}>
          {tokenStateMessage.description}
        </Alert>

        <FormReturnLink href={paths.forgotPassword} label="Nieuwe resetlink aanvragen" />
        <FormReturnLink href={paths.login} label="Terug naar inloggen" />
      </>
    );
  }

  return (
    <>
      <FormHead
        icon={<PasswordIcon />}
        title="Nieuw wachtwoord instellen"
        description="Kies een nieuw wachtwoord voor je klantenportaalaccount."
      />

      {serverError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {serverError}
        </Alert>
      )}

      <Form methods={methods} onSubmit={onSubmit}>
        <Stack spacing={3}>
          <Field.Text
            name="password"
            label="Nieuw wachtwoord"
            placeholder={`Minimaal ${PORTAL_PASSWORD_MIN_LENGTH} tekens`}
            helperText={PORTAL_PASSWORD_MIN_LENGTH_HINT}
            type="password"
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Field.Text
            name="confirmPassword"
            label="Herhaal wachtwoord"
            placeholder="Herhaal je wachtwoord"
            type="password"
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Button
            fullWidth
            color="primary"
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            loadingIndicator="Wachtwoord opslaan..."
          >
            Wachtwoord opslaan
          </Button>
        </Stack>
      </Form>

      <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
        Deze resetflow verloopt via een beveiligde link. Is de link verlopen, vraag dan opnieuw een
        resetlink aan.
      </Typography>
    </>
  );
}
